import { create } from "zustand";

/**
 * Trace-suppression counter — debug observability for the trace-mode-strip
 * feature. Increments every time the ChatFeed render-item layer hides a
 * status/insight/trace item because `traceMode === "disabled"`.
 *
 * Displayed in the Dev Debug Panel next to the `traceMode` selector so
 * engineers can see the impact of flipping `disabled` without needing to
 * scroll through the chat. Session-scoped; resets when the session resets.
 *
 * Not wired into analytics — this is dev-only. See
 * docs/plans/2026-04-23-flash-proposal-trace-mode-strip_flash.md §Measurement
 * plan (debug panel observability).
 */

export type TraceSuppressionKind = "status" | "insight" | "trace-group";

export type TraceSuppressionCounts = {
  status: number;
  insight: number;
  traceGroup: number;
};

type TraceSuppressionStore = TraceSuppressionCounts & {
  increment: (kind: TraceSuppressionKind) => void;
  reset: () => void;
};

const ZERO: TraceSuppressionCounts = { status: 0, insight: 0, traceGroup: 0 };

export const useTraceSuppressionStore = create<TraceSuppressionStore>((set) => ({
  ...ZERO,
  increment: (kind) =>
    set((s) => {
      if (kind === "status") return { status: s.status + 1 };
      if (kind === "insight") return { insight: s.insight + 1 };
      return { traceGroup: s.traceGroup + 1 };
    }),
  reset: () => set({ ...ZERO }),
}));

/**
 * Module-level increment — safe to call from non-hook contexts (e.g. inline
 * in a switch case during render). No-op on the server. Reading the store
 * from a React component uses `useTraceSuppressionStore(...)` as normal.
 */
export function incrementTraceSuppression(kind: TraceSuppressionKind): void {
  if (typeof window === "undefined") return;
  useTraceSuppressionStore.getState().increment(kind);
}
